import React from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { timeAgo, formatDate } from "../../shared/utils/format";
import { useUserSection } from "./useUserSection";
import { LoadState } from "./ui";
import { useSearchParamUpdater } from "../../shared/hooks/useSearchParamUpdater";
import { BrandKitTab } from "./BrandKitTab";
import { currentSubscription } from "../../shared/utils/subscription";
import { OverviewTab, PlanStateBadge } from "./OverviewTab";
import { ErrorsTab } from "./ErrorsTab";
import { AccountsTab, BillingTab, BlogsTab, CampaignsTab, CreditsTab, EmailsTab, PostsTab, TopicsTab, WeeksTab } from "./ListTabs";
import type { UserHeader } from "./types";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "brand-kit", label: "Brand kit" },
  { key: "posts", label: "Posts" },
  { key: "weeks", label: "Weeks" },
  { key: "topics", label: "Topics" },
  { key: "campaigns", label: "Ad campaigns" },
  { key: "accounts", label: "Accounts" },
  { key: "blogs", label: "Blogs" },
  { key: "billing", label: "Billing" },
  { key: "credits", label: "Credits" },
  { key: "emails", label: "Emails" },
  { key: "errors", label: "Errors" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

// Sections that are per-brand; credits and emails belong to the user.
const BRAND_SCOPED: ReadonlySet<TabKey> = new Set<TabKey>(["brand-kit", "weeks", "posts", "topics", "campaigns", "accounts", "blogs", "errors"]);

const isTabKey = (value: string | null): value is TabKey => TABS.some((t) => t.key === value);

const TAB_COMPONENTS: Record<Exclude<TabKey, "overview">, React.FC<{ userId: number; clientId?: number }>> = {
  "brand-kit": BrandKitTab,
  weeks: WeeksTab,
  posts: PostsTab,
  topics: TopicsTab,
  campaigns: CampaignsTab,
  accounts: AccountsTab,
  blogs: BlogsTab,
  billing: BillingTab,
  credits: CreditsTab,
  emails: EmailsTab,
  errors: ErrorsTab,
};

function UserDetailsContent({ userId }: Readonly<{ userId: number }>) {
  // Tab, brand, page and status filter live in the URL so a refresh or back/forward keeps the view.
  const [searchParams] = useSearchParams();
  const updateSearchParams = useSearchParamUpdater();
  const tabParam = searchParams.get("tab");
  const tab: TabKey = isTabKey(tabParam) ? tabParam : "overview";
  const brandParam = Number(searchParams.get("brand")) || undefined;
  const header = useUserSection<UserHeader>(userId, "");

  const selectTab = (key: TabKey) =>
    updateSearchParams({ tab: key === "overview" ? null : key, page: null, status: null, platform: null, from: null, to: null });
  const selectBrand = (id: number | undefined) => updateSearchParams({ brand: id, page: null, status: null });

  if (!header.data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        <LoadState loading={header.loading} error={header.error} onRetry={header.reload} />
      </div>
    );
  }

  const { user, clients, subscriptions } = header.data;
  const clientId = clients.some((c) => c.id === brandParam) ? brandParam : undefined;
  const plan = currentSubscription(subscriptions);
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || "Unnamed user";
  const TabComponent = tab === "overview" ? null : TAB_COMPONENTS[tab];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{name}</h1>
            {user.verified ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500" />
            )}
          </div>
          <p className="text-sm text-gray-500">
            {user.email} · #{user.id} · joined {formatDate(user.createdAt)}
            {user.lastLoginAt && ` · active ${timeAgo(user.lastLoginAt)}`}
          </p>
          <p className="mt-1.5 flex items-center gap-2 text-sm text-gray-700">
            {plan ? (
              <>
                <span className="font-medium">{plan.prices?.plans.name ?? "Unknown plan"}</span>
                <PlanStateBadge sub={plan} />
              </>
            ) : (
              <span className="text-gray-400">No subscription</span>
            )}
          </p>
        </div>
        {BRAND_SCOPED.has(tab) && clients.length > 1 && (
          <select
            value={clientId ?? ""}
            onChange={(e) => selectBrand(e.target.value ? Number(e.target.value) : undefined)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
          >
            <option value="">All brands ({clients.length})</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.businessName || `Brand #${c.id}`}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => selectTab(t.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-violet-600 text-violet-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {TabComponent ? (
        <TabComponent
          key={`${tab}-${clientId ?? "all"}`}
          userId={userId}
          clientId={BRAND_SCOPED.has(tab) ? clientId : undefined}
        />
      ) : (
        <OverviewTab userId={userId} header={header.data} />
      )}
    </div>
  );
}

const UserDetailsScreen: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const id = Number(userId);
  const fromUsers = (useLocation().state as { usersSearch?: string } | null)?.usersSearch ?? "";

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <Link to={`/users${fromUsers}`} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="w-4 h-4" /> Back to users
      </Link>
      {Number.isInteger(id) && id > 0 ? (
        <UserDetailsContent key={id} userId={id} />
      ) : (
        <p className="text-sm text-gray-500">Invalid user id.</p>
      )}
    </div>
  );
};

export default UserDetailsScreen;
